/**
 * Agency console shell (founder 2026-07-05: "a full new area for admin —
 * that's how every other software operates"). The operator's OWN app:
 * its own sidebar (Portfolio / Clients / Feedback & Fixes), no client nav,
 * no client onboarding nags. You go INTO a client from here — the switcher
 * picks the account, "Open client dashboard" drops you into the client
 * shell — and the client shell's "Agency console" link brings you back.
 *
 * Operator-gated twice: every /admin API endpoint 403s non-operators
 * server-side; this shell additionally redirects non-operators to their
 * dashboard so a client can never even see the empty frame.
 */

import { useQuery } from "@tanstack/react-query";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { useApiClient } from "../lib/api-context.js";
import { AccountSwitcher } from "./AppLayout.js";

export default function AdminLayout(): React.ReactElement {
  const api = useApiClient();
  const location = useLocation();

  const { data: agency, isLoading, isError, refetch } = useQuery({
    queryKey: ["account", "agency-status"],
    queryFn: () => api.get<{ isOperator: boolean }>("/account/agency-status"),
    staleTime: 5 * 60_000,
  });

  // Live error count for the Issues nav badge — a broken client pipeline must
  // be visible from ANY console page, not just when the feed happens to be open.
  const { data: issuesData } = useQuery({
    queryKey: ["admin", "issues"],
    enabled: Boolean(agency?.isOperator),
    queryFn: () => api.get<{ activeCount: number }>("/admin/issues"),
    refetchInterval: 60_000,
    staleTime: 60_000,
  });
  const activeIssues = issuesData?.activeCount ?? 0;

  // Data-health badge — how many clients have a math problem (a red finding).
  // Same reasoning as Issues: a client whose numbers are wrong must be visible
  // from any console page.
  const { data: healthData } = useQuery({
    queryKey: ["admin", "data-health"],
    enabled: Boolean(agency?.isOperator),
    queryFn: () => api.get<{ redAccounts: number; attentionAccounts: number }>("/admin/data-health"),
    refetchInterval: 5 * 60_000,
    staleTime: 5 * 60_000,
  });
  const redAccounts = healthData?.redAccounts ?? 0;
  const healthAttention = healthData?.attentionAccounts ?? redAccounts;

  // Cards badge — how many client-facing cards still need the operator's review
  // (pending + changed-since-approval). A to-do count, not a problem, so it's
  // toned neutral (accent) rather than red like Issues/Data-health.
  const { data: cardsData } = useQuery({
    queryKey: ["admin", "cards"],
    enabled: Boolean(agency?.isOperator),
    queryFn: () => api.get<{ pendingCount: number }>("/admin/cards"),
    refetchInterval: 5 * 60_000,
    staleTime: 5 * 60_000,
  });
  const pendingCards = cardsData?.pendingCount ?? 0;

  if (isLoading) {
    return <p style={{ color: "var(--body)", padding: 24 }}>Loading…</p>;
  }
  // A FAILED check is not the same as "not an operator" — bouncing the operator
  // to a client dashboard on a rate-limit blip reads as "the console vanished".
  // Only a confirmed isOperator:false redirects.
  if (isError || !agency) {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: "var(--body)", margin: 0 }}>Couldn't check agency access just now.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          style={{
            marginTop: 10,
            padding: "7px 14px",
            borderRadius: 8,
            border: "1px solid var(--border)",
            background: "var(--surface)",
            color: "var(--text)",
            fontSize: 13.5,
            cursor: "pointer",
          }}
        >
          Try again
        </button>
      </div>
    );
  }
  if (!agency.isOperator) {
    return <Navigate to="/dashboard" replace />;
  }

  const navItems: Array<{ to: string; label: string; end: boolean; badge?: number; badgeTone?: "bad" | "accent" }> = [
    { to: "/agency", label: "Portfolio", end: true },
    { to: "/admin", label: "Clients", end: true },
    { to: "/agency/issues", label: "Issues", end: false, badge: activeIssues },
    {
      to: "/agency/data-health",
      label: "Data health",
      end: false,
      badge: healthAttention,
      badgeTone: redAccounts > 0 ? "bad" : "accent",
    },
    { to: "/agency/cards", label: "Cards", end: false, badge: pendingCards, badgeTone: "accent" },
    { to: "/agency/feedback", label: "Feedback & Fixes", end: false },
  ];

  return (
    <div data-testid="agency-console-fixed-theme" style={styles.root}>
      <aside style={styles.sidebar}>
        <div style={styles.brand}>
          <span style={styles.brandMark} aria-hidden="true" />
          <div>
            <strong style={{ fontFamily: "var(--font-display)", fontSize: 15, display: "block" }}>Scale Advisors</strong>
            <span style={styles.consoleChip}>AGENCY CONSOLE</span>
          </div>
        </div>

        <nav style={styles.nav}>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className="dd-nav-item"
              style={({ isActive }) => ({ ...styles.navLink, ...(isActive ? styles.navLinkActive : {}) })}
            >
              <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                {item.label}
                {(item.badge ?? 0) > 0 && (
                  <span style={item.badgeTone === "accent" ? styles.navBadgeAccent : styles.navBadge}>{item.badge}</span>
                )}
              </span>
            </NavLink>
          ))}
        </nav>

        <div style={styles.clientSection}>
          <div style={styles.clientLabel}>Work inside a client</div>
          <AccountSwitcher />
          <Link to="/dashboard" style={styles.openClient}>
            Open client dashboard →
          </Link>
          <p style={styles.clientHint}>
            The switcher picks which client; the link drops you into their app. Their sidebar has an
            "Agency console" link to bring you back here.
          </p>
        </div>
      </aside>

      <main style={styles.main} key={location.pathname}>
        <Outlet />
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  // The agency console is Scale Advisors' own app. Scope its primary color here
  // so the active client's brand color never bleeds into operator pages.
  root: {
    display: "flex",
    minHeight: "100vh",
    background: "var(--bg)",
    "--accent": "#15b8a6",
    "--accent-contrast": "#04211d",
  } as React.CSSProperties,
  sidebar: {
    width: 240,
    flexShrink: 0,
    padding: "22px 16px",
    borderRight: "1px solid var(--border)",
    background: "var(--surface)",
    display: "flex",
    flexDirection: "column",
    gap: 18,
    position: "sticky",
    top: 0,
    alignSelf: "flex-start",
    height: "100vh",
    overflowY: "auto",
  },
  brand: { display: "flex", alignItems: "center", gap: 10 },
  brandMark: {
    width: 26,
    height: 26,
    borderRadius: 7,
    background: "linear-gradient(135deg, var(--accent), color-mix(in srgb, var(--accent) 55%, #7C3AED))",
    display: "inline-block",
    flexShrink: 0,
  },
  consoleChip: {
    fontSize: 9.5,
    letterSpacing: 1.2,
    fontWeight: 700,
    color: "var(--accent)",
    background: "color-mix(in srgb, var(--accent) 14%, transparent)",
    padding: "2px 6px",
    borderRadius: 4,
    display: "inline-block",
    marginTop: 3,
  },
  nav: { display: "flex", flexDirection: "column", gap: 2 },
  navLink: {
    display: "block",
    padding: "9px 12px",
    borderRadius: 8,
    color: "var(--body)",
    textDecoration: "none",
    fontSize: 14,
  },
  navLinkActive: {
    background: "color-mix(in srgb, var(--accent) 13%, transparent)",
    color: "var(--text)",
    fontWeight: 600,
  },
  navBadge: {
    fontSize: 11,
    fontWeight: 700,
    minWidth: 18,
    textAlign: "center",
    padding: "1px 6px",
    borderRadius: 9,
    background: "color-mix(in srgb, var(--bad) 16%, transparent)",
    color: "var(--bad)",
  },
  navBadgeAccent: {
    fontSize: 11,
    fontWeight: 700,
    minWidth: 18,
    textAlign: "center",
    padding: "1px 6px",
    borderRadius: 9,
    background: "color-mix(in srgb, var(--accent) 16%, transparent)",
    color: "var(--accent)",
  },
  clientSection: {
    marginTop: "auto",
    borderTop: "1px solid var(--border)",
    paddingTop: 14,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  clientLabel: { fontSize: 11, letterSpacing: 0.8, fontWeight: 700, color: "var(--body-faint)", textTransform: "uppercase" },
  openClient: { color: "var(--accent)", fontSize: 13.5, textDecoration: "none", fontWeight: 600 },
  clientHint: { fontSize: 11.5, color: "var(--body-faint)", lineHeight: 1.45, margin: 0 },
  main: { flex: 1, minWidth: 0, padding: "28px 32px 64px" },
};
